import { PointArray } from '@editor/interfaces/attribute'
import { PolylineProps } from '@editor/interfaces/canvas'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

/**
 * Distance label props specifically for working cylinder.
 */
export interface DistanceLabelWorkingCylinderProps extends Omit<PolylineProps, 'label' | 'segmentLabels'> {
  /**
   * Cylinder diameter.
   */
  diameter?: number
}

export interface RebarDetectionState {
  /**
   * Flag to indicate if the tool is dirty (has working items).
   */
  isDirty: boolean

  /**
   * Rebar being processed.
   */
  isSaving: boolean

  /**
   * Working distance labels that user draws to mark the cylinder.
   */
  workingLines: DistanceLabelWorkingCylinderProps[]

  /**
   * Cylinder diameter.
   */
  baseDiameter?: number
}

const initialState: RebarDetectionState = {
  isDirty: false,
  isSaving: false,
  workingLines: [],
}

export const slice = createSlice({
  name: 'editor/tools/rebarDetaction',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload
    },
    setWorkingLines: (state, action: PayloadAction<DistanceLabelWorkingCylinderProps[]>) => {
      state.workingLines = action.payload
      if (action.payload.length > 0) {
        state.isDirty = true
      }
    },
    addWorkingLines: (state, action: PayloadAction<DistanceLabelWorkingCylinderProps>) => {
      state.workingLines.push(action.payload)
      state.isDirty = true
    },
    completeLastWorkingLines: (state, { payload }: PayloadAction<PointArray>) => {
      const lastIndex = state.workingLines.length - 1
      if (lastIndex >= 0) {
        state.workingLines[lastIndex].points[1] = payload
      }
    },
    updateWorkingLinesPointsByIndex: (
      state,
      { payload: { index, points } }: PayloadAction<{ index: number; points: PointArray[] }>,
    ) => {
      state.workingLines[index].points = points
    },
    updateWorkingLinesDiameterById: (
      state,
      { payload: { id, diameter } }: PayloadAction<{ id: string; diameter: number }>,
    ) => {
      state.workingLines = state.workingLines.map((label) => (label.id === id ? { ...label, diameter } : label))
    },
    undoWorkingLine: (state) => {
      const lastWorkingLine = state.workingLines[state.workingLines.length - 1]
      if (lastWorkingLine) {
        if (lastWorkingLine.points.length >= 2) {
          // Remove the last point
          lastWorkingLine.points.pop()
        } else {
          // Remove the entire working line
          state.workingLines.pop()
        }
      }

      if (state.workingLines.length === 0) {
        state.isDirty = false
      }
    },
    deleteWorkingLinesAtIndex: (state, action: PayloadAction<number>) => {
      state.workingLines.splice(action.payload, 1)
      if (state.workingLines.length === 0) {
        state.isDirty = false
      }
    },

    /**
     * Set base diameter for new distance labels.
     * Will also set the diameter for existing distance labels if they don't have one.
     * Will also set the diameter for selected distance label.
     */
    setDiameter: (state, action: PayloadAction<number>) => {
      state.baseDiameter = action.payload
      state.workingLines = state.workingLines.map((label) => ({
        ...label,
        diameter: label.diameter || action.payload,
      }))
    },

    /**
     * Full reset of working state only if there are no working distance labels.
     * This is done here instead of inside hooks to prevent unnecessary rendering.
     */
    resetFullIfNoWorkingLabels: (state) => {
      if (state.workingLines.length === 0) {
        state.isSaving = false
        state.workingLines = []
        state.isDirty = false
      }
    },

    /**
     * Full reset of working state.
     */
    resetWorking: (state) => {
      state.isSaving = false
      state.workingLines = []
      state.isDirty = false
    },
    reset: () => initialState,
  },
})

export const {
  setIsLoading,
  reset,
  resetWorking,
  resetFullIfNoWorkingLabels,
  setDiameter,

  // distance labels
  setWorkingLines,
  addWorkingLines,
  completeLastWorkingLines,
  updateWorkingLinesPointsByIndex,
  updateWorkingLinesDiameterById,
  undoWorkingLine,
  deleteWorkingLinesAtIndex,
} = slice.actions

export default slice.reducer
